/**
 * Stock Usage Report Page
 *
 * Always-visible date-range filter bar (same layout as the Reports hub):
 * custom date inputs + Apply on row 1, quick-range preset chips on row 2.
 * Filtering/pagination is handled declaratively by HTMX — the chips and
 * the datepickers just mutate the form state then submit it.
 *
 * Preset model (shared contract with the Reports filter bar):
 *   - A named ``preset`` (hidden input #usage-preset) wins server-side
 *     over the explicit date_from/date_to inputs.
 *   - Clicking a chip sets the preset, clears the date inputs and submits.
 *   - Manually picking a date flips the preset back to ``custom`` so the
 *     typed range is honoured.
 */

const ACTIVE_CHIP_CLASSES = [
  'bg-primary-50', 'text-primary-700', 'border-primary-300',
  'dark:bg-primary-900/30', 'dark:text-primary-300', 'dark:border-primary-700',
];
const INACTIVE_CHIP_CLASSES = [
  'bg-white', 'text-gray-600', 'border-gray-200', 'hover:bg-gray-50',
  'dark:bg-gray-800', 'dark:text-gray-400', 'dark:border-gray-700', 'dark:hover:bg-gray-700',
];

/**
 * Initialize the stock usage report page
 */
export function initStockUsage() {
  initPresetChips();
  initDatepickerBridge();
  bindAfterSettleOnce();
}

function setChipActive(chip, active) {
  const add = active ? ACTIVE_CHIP_CLASSES : INACTIVE_CHIP_CLASSES;
  const remove = active ? INACTIVE_CHIP_CLASSES : ACTIVE_CHIP_CLASSES;
  chip.classList.remove(...remove);
  chip.classList.add(...add);
}

function highlightPreset(form, value) {
  form.querySelectorAll('.usage-preset-btn').forEach(function (chip) {
    setChipActive(chip, chip.dataset.usagePreset === value);
  });
}

function clearDateInputs(form) {
  form.querySelectorAll('input[name="date_from"], input[name="date_to"]').forEach(function (input) {
    input.value = '';
  });
}

function submitForm(form) {
  form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}

/**
 * Wire the quick-range preset chips: set the hidden preset, clear the
 * date inputs (the preset drives the range) and submit.
 */
function initPresetChips() {
  var form = document.getElementById('usageFilterForm');
  if (!form) return;
  var preset = form.querySelector('#usage-preset');

  form.querySelectorAll('.usage-preset-btn').forEach(function (chip) {
    if (chip._suPresetBound) return;
    chip._suPresetBound = true;
    chip.addEventListener('click', function () {
      var value = chip.dataset.usagePreset;
      if (preset) preset.value = value;
      clearDateInputs(form);
      highlightPreset(form, value);
      submitForm(form);
    });
  });
}

/**
 * Bridge Flowbite datepicker's changeDate event to a native change event
 * so HTMX's hx-trigger can pick it up. A manual date pick also flips the
 * preset back to ``custom`` and de-highlights every chip.
 */
function initDatepickerBridge() {
  var form = document.getElementById('usageFilterForm');
  if (!form) return;
  var preset = form.querySelector('#usage-preset');

  form.querySelectorAll('input[datepicker]').forEach(function (input) {
    if (input._suDpBound) return;
    input._suDpBound = true;
    input.addEventListener('changeDate', function () {
      if (preset) preset.value = 'custom';
      highlightPreset(form, 'custom');
      this.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

let afterSettleBound = false;

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', function (e) {
    var target = e.detail && e.detail.target;
    if (!target || target.id !== 'list-results') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
  });
}

/**
 * Auto-init entry point (wired via ``data-auto-init="stockUsage"``).
 */
export function autoInitStockUsage() {
  const root = document.querySelector('[data-auto-init="stockUsage"]');
  if (!root) return;
  initStockUsage();
}

window.initStockUsage = initStockUsage;
